import React, { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useIdTemple, TemplePicker, PageHead, EventNameEditor } from "../components/IdentityKit";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, EyeOff, QrCode } from "lucide-react";
import { toast } from "sonner";

const CFG = {
  departments: {
    title: "Departments", eyebrow: "Master Data", sub: "Government / temple departments coordinating duties. The colour is used on Duty Pass cards.", one: "Department",
    fields: [["name", "Department Name", "text", true, true], ["head_name", "Head Name"], ["head_mobile", "Head Mobile", "tel"], ["color", "Department Colour", "color"],
      ["sign_label", "Signing officer on this department's cards (optional, e.g. Superintendent of Police)", "text", false, true],
      ["signature", "Signature picture (optional)", "sig", false, true], ["description", "Description", "area", false, true]],
    cols: [["name", "Name"], ["head_name", "Head"], ["head_mobile", "Mobile"], ["color", "Colour"], ["sign_label", "Signing Officer"]],
    blank: { color: "#1E3A8A" },
  },
  locations: {
    title: "Locations / Ghats", eyebrow: "Master Data", sub: "Ghats, gates, parking and other duty points. The colour is used on Vehicle Pass bands; hidden ones are not offered in forms.", one: "Location / Ghat",
    fields: [["name", "Name", "text", true, true], ["kind", "Type", "kind"], ["color", "Colour (vehicle pass band)", "color"], ["latitude", "Latitude"], ["longitude", "Longitude"],
      ["map_url", "Map URL", "text", false, true], ["description", "Description", "area", false, true], ["hidden", "Hidden (not offered in forms)", "check"]],
    cols: [["name", "Name"], ["kind", "Type"], ["color", "Colour"], ["latlng", "Location"], ["hidden", ""]],
    blank: { kind: "GHAT", color: "#0E7490" },
  },
  sacred: {
    title: "Sacred Locations", eyebrow: "Master Data", sub: "Sacred places of this temple / festival with an ornamental gateway illustration and display order.", one: "Sacred Location",
    fields: [["name", "Name (English)", "text", true], ["name_telugu", "Name (Telugu)"], ["district", "District"], ["category", "Category", "text", false, false, "Ghat / Temple / Parking / Camp"],
      ["order", "Display Order", "number"], ["image", "Gateway Illustration", "image", false, true], ["description", "Description", "area", false, true], ["hidden", "Hidden", "check"]],
    cols: [["order", "#"], ["image", ""], ["name", "Name"], ["name_telugu", "Telugu"], ["district", "District"], ["category", "Category"], ["hidden", ""]],
    blank: { order: 100 },
  },
};

function MasterDialog({ cfg, m, open, item, templeId, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(item ? { ...item } : { ...cfg.blank }); }, [open, item, cfg]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      if (item) await api.put(`/identity/masters/${m}/${item.id}`, f);
      else await api.post(`/identity/masters/${m}`, { ...f, temple_id: templeId });
      toast.success(`${cfg.one} saved`); onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="id-master-dialog">
        <DialogHeader><DialogTitle className="font-heading text-maroon-900">{item ? "Edit" : "New"} {cfg.one}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {cfg.fields.map(([k, label, type = "text", , full, ph]) => (
            <div key={k} className={full ? "col-span-2" : ""}>
              {type === "check" ? (
                <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} /> {label}</label>
              ) : <Label className="text-xs">{label}</Label>}
              {type === "area" && <Textarea rows={2} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} />}
              {type === "color" && <div>
                <div className="flex gap-2 items-center"><input type="color" value={f[k] || "#0077B6"} onChange={(e) => set(k, e.target.value)} className="h-10 w-16 rounded border" /><Input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="font-mono" /></div>
                <ColorPalette value={f[k]} onPick={(c) => set(k, c)} />
              </div>}
              {type === "kind" && (
                <select value={f[k] || "GHAT"} onChange={(e) => set(k, e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                  {["GHAT", "LOCATION", "GATE", "PARKING", "TEMPLE"].map((o) => <option key={o} value={o}>{o[0] + o.slice(1).toLowerCase()}</option>)}
                </select>
              )}
              {type === "image" && (
                <div className="flex items-center gap-3">
                  {f[k] ? <img src={f[k]} alt="" className="h-20 w-28 object-contain rounded border bg-white" /> : <div className="h-20 w-28 rounded border border-dashed flex items-center justify-center text-[11px] text-slate-400">No image</div>}
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs text-maroon-700 underline cursor-pointer">Upload picture
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        if (file.size > 1.4 * 1024 * 1024) { toast.error("Picture too large (max 1.4 MB)"); return; }
                        const rd = new FileReader(); rd.onload = () => set(k, rd.result); rd.readAsDataURL(file);
                      }} />
                    </label>
                    <Input value={String(f[k] || "").startsWith("data:") ? "" : f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="or https://… link" />
                    {f[k] && <button type="button" className="text-xs text-red-600 underline text-left" onClick={() => set(k, "")}>Remove</button>}
                  </div>
                </div>
              )}
              {type === "sig" && (
                <div className="flex items-center gap-3">
                  {f[k] ? <img src={f[k]} alt="" className="h-12 max-w-[160px] object-contain rounded border bg-white" /> : <span className="text-[11px] text-slate-400">None - the temple's card signature is used</span>}
                  <label className="text-xs text-maroon-700 underline cursor-pointer">{f[k] ? "Replace" : "Upload"}
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
                      if (file.size > 500 * 1024) { toast.error("Signature picture too large (max 500 KB)"); return; }
                      const rd = new FileReader(); rd.onload = () => set(k, rd.result); rd.readAsDataURL(file);
                    }} />
                  </label>
                  {f[k] && <button type="button" className="text-xs text-red-600 underline" onClick={() => set(k, "")}>Remove</button>}
                </div>
              )}
              {["text", "tel", "number"].includes(type) && <Input type={type} value={f[k] ?? ""} placeholder={ph || ""} onChange={(e) => set(k, e.target.value)} data-testid={`id-m-${k}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-master-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RangeDialog({ open, item, templeId, temples, user, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(item ? { ...item } : { temple_id: templeId, prefix: "", total_digits: 10, start_number: 1, end_number: 9999999, description: "" });
  }, [open, item, templeId]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const prefix = String(f.prefix || "").toLowerCase();
  const digits = Number(f.total_digits || 10) - prefix.length;
  const label = (nn) => (digits > 0 ? `${prefix}${String(nn || 0).padStart(digits, "0")}` : "-");
  const cap = Math.max(0, Number(f.end_number || 0) - Number(f.start_number || 0) + 1);
  const save = async () => {
    setBusy(true);
    try {
      if (item) await api.put(`/identity/tag-ranges/${item.id}`, f); else await api.post("/identity/tag-ranges", f);
      toast.success("Tag range saved"); onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="id-range-dialog">
        <DialogHeader><DialogTitle className="font-heading text-maroon-900">{item ? "Edit" : "New"} Tag Range</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {user.role === "SUPER_ADMIN" && !item && (
            <div className="col-span-2"><Label className="text-xs">Temple</Label>
              <select value={f.temple_id || ""} onChange={(e) => set("temple_id", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                {temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
              </select></div>
          )}
          <div><Label className="text-xs">Prefix (letters / digits)</Label><Input value={f.prefix || ""} onChange={(e) => set("prefix", e.target.value.toLowerCase())} placeholder="ssd" data-testid="id-range-prefix" /></div>
          <div><Label className="text-xs">Total length (whole tag)</Label><Input type="number" value={f.total_digits || ""} onChange={(e) => set("total_digits", e.target.value)} /></div>
          <div><Label className="text-xs">Start Number</Label><Input type="number" value={f.start_number ?? ""} onChange={(e) => set("start_number", e.target.value)} /></div>
          <div><Label className="text-xs">End Number</Label><Input type="number" value={f.end_number ?? ""} onChange={(e) => set("end_number", e.target.value)} /></div>
          <div className="col-span-2"><Label className="text-xs">Description (optional)</Label><Textarea rows={2} value={f.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Internal notes for officers" /></div>
        </div>
        <div className="rounded-lg bg-[#FFF4E6] border border-[#F6D5BC] p-3 text-sm">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#C2410C] mb-1">Preview</p>
          <p className="flex flex-wrap items-center gap-2"><QrCode className="w-4 h-4 text-[#C2410C]" /> First tag <code className="bg-white px-2 py-0.5 rounded border">{label(f.start_number)}</code> … through … <code className="bg-white px-2 py-0.5 rounded border">{label(f.end_number)}</code></p>
          <p className="text-xs text-slate-500 mt-1">Capacity: {cap.toLocaleString("en-IN")} tags</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-range-save">Save Range</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TagRanges({ user, temples, templeId }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/identity/tag-ranges", { params: { temple_id: templeId } }).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, [templeId]);
  useEffect(load, [load]);
  const del = async (r) => {
    if (!window.confirm(`Delete range ${r.first_tag} - ${r.last_tag}?`)) return;
    try { await api.delete(`/identity/tag-ranges/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const tname = (tid) => temples.find((t) => t.temple_id === tid)?.name || tid;
  return (
    <>
      <PageHead eyebrow="Children Tag · QR Serials" title="Tag Ranges" subtitle="Valid QR serial numbers for the pre-printed children safety tags, per temple. Example: ssd0000001.">
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-range-new"><Plus className="w-4 h-4 mr-1" /> Add New Range</Button>
      </PageHead>
      <Card><CardContent className="p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="id-ranges-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <th className="py-2 pr-3">Prefix</th><th className="py-2 pr-3">Temple</th><th className="py-2 pr-3">Range</th><th className="py-2 pr-3">Next Tag</th><th className="py-2 pr-3">Used / Capacity</th><th className="py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No tag ranges yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#F1EBE3]">
                <td className="py-2 pr-3"><span className="px-2 py-0.5 rounded bg-slate-100 font-bold uppercase text-xs">{r.prefix}</span></td>
                <td className="py-2 pr-3">{tname(r.temple_id)}</td>
                <td className="py-2 pr-3 font-mono text-xs">{r.first_tag} → {r.last_tag}</td>
                <td className="py-2 pr-3 font-mono text-xs font-bold text-[#C2410C]">{r.next_tag || "Full"}</td>
                <td className="py-2 pr-3 min-w-[180px]">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#E8590C]" style={{ width: `${Math.min(100, (r.used / r.capacity) * 100)}%` }} /></div>
                  <span className="text-xs text-slate-500">{r.used.toLocaleString("en-IN")} / {r.capacity.toLocaleString("en-IN")}</span>
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <RangeDialog open={open} item={edit} templeId={templeId} temples={temples} user={user} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </>
  );
}

// Ready-made department colours (dark to light shades, all print well on the ID cards); one click picks one.
const PALETTE = [
  ["Blue", ["#1C4E94", "#2463BA", "#2F7BDE", "#4C90F0", "#8ABBFF"]],
  ["Red", ["#8E2023", "#AC2F33", "#CD4246", "#E76A6E", "#FA999C"]],
  ["Green", ["#165A36", "#1C6E42", "#238551", "#32A467", "#72CA9B"]],
  ["Orange", ["#77450D", "#935610", "#C87619", "#EC9A3C", "#FBB360"]],
  ["Purple", ["#5C255C", "#7C327C", "#9D3F9D", "#BD6BBD", "#D69FD6"]],
  ["Teal", ["#004D46", "#007067", "#00A396", "#13C9BA", "#7AE1D8"]],
  ["Maroon", ["#4A0A1C", "#6B0F2A", "#7A0F2E", "#9E1B3F", "#C2546F"]],
  ["Gold", ["#5C4405", "#866103", "#C29404", "#D1980B", "#F0B726"]],
  ["Brown", ["#4D3016", "#63411E", "#7A542E", "#97704A", "#B88E66"]],
  ["Grey", ["#1C2127", "#383E47", "#5F6B7C", "#8F99A8", "#C5CBD3"]],
];
function ColorPalette({ value, onPick }) {
  const cur = String(value || "").toLowerCase();
  return (
    <div className="mt-2 grid grid-cols-5 sm:grid-cols-10 gap-x-2.5 gap-y-1.5" data-testid="color-palette">
      {PALETTE.map(([name, shades]) => (
        <div key={name} className="flex flex-col gap-1" title={name}>
          <span className="text-[8px] leading-none font-semibold text-slate-500 text-center whitespace-nowrap -mx-1">{name}</span>
          {shades.map((c, i) => (
            <button key={c} type="button" onClick={() => onPick(c)} title={`${name}-${i + 1} ${c}`} data-testid={`color-${name.toLowerCase()}-${i + 1}`}
              className={`h-6 rounded text-[9px] font-semibold ${i < 3 ? "text-white" : "text-slate-800"} ${cur === c.toLowerCase() ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
              style={{ background: c }}>{i + 1}</button>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function IdentityMasters({ section }) {
  const { user } = useAuth();
  const { temples, templeId, setTempleId } = useIdTemple(user);
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const cfg = CFG[section];
  const load = useCallback(() => {
    if (!templeId || !cfg) return;
    api.get(`/identity/masters/${section}`, { params: { temple_id: templeId } }).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [section, templeId, cfg]);
  useEffect(load, [load]);

  if (section === "tag-ranges") return (
    <div data-testid="id-masters-tag-ranges">
      {user.role === "SUPER_ADMIN" && <div className="mb-2"><TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} /></div>}
      <TagRanges key={templeId} user={user} temples={temples} templeId={templeId} />
    </div>
  );

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    try { await api.delete(`/identity/masters/${section}/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const cell = (r, c) => {
    if (c === "color") return r.color ? <span className="inline-flex items-center gap-2"><span className="w-5 h-5 rounded border" style={{ background: r.color }} /><code className="text-xs">{r.color}</code></span> : "-";
    if (c === "hidden") return r.hidden ? <span className="inline-flex items-center gap-1 text-xs text-slate-500"><EyeOff className="w-3.5 h-3.5" /> Hidden</span> : "";
    if (c === "latlng") return r.latitude != null ? <a href={r.map_url || `https://maps.google.com/?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="text-maroon-700 underline inline-flex items-center gap-1 text-xs"><MapPin className="w-3.5 h-3.5" />{r.latitude}, {r.longitude}</a> : r.map_url ? <a href={r.map_url} target="_blank" rel="noreferrer" className="text-maroon-700 underline text-xs">Map</a> : "-";
    if (c === "kind") return r.kind ? r.kind[0] + r.kind.slice(1).toLowerCase() : "-";
    if (c === "image") return r.image ? <img src={r.image} alt="" className="h-10 w-14 object-contain rounded" /> : "";
    if (c === "name_telugu") return <span className="font-telugu text-base">{r.name_telugu || "-"}</span>;
    return r[c] ?? "-";
  };
  return (
    <div data-testid={`id-masters-${section}`}>
      <PageHead eyebrow={cfg.eyebrow} title={cfg.title} subtitle={cfg.sub}>
        <TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} />
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="bg-[#E8590C] hover:bg-[#C2410C] text-white" data-testid="id-master-new"><Plus className="w-4 h-4 mr-1" /> Add {cfg.one}</Button>
      </PageHead>
      {section === "departments" && <div className="mb-3"><EventNameEditor templeId={templeId} temples={temples} /></div>}
      <Card><CardContent className="p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="id-masters-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
            {cfg.cols.map(([, l], i) => <th key={i} className="py-2 pr-3">{l}</th>)}<th className="py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cfg.cols.length + 1} className="py-10 text-center text-slate-400">Nothing added yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={`border-b border-[#F1EBE3] ${r.hidden ? "opacity-60" : ""}`}>
                {cfg.cols.map(([c], i) => <td key={i} className={`py-2 pr-3 ${c === "name" ? "font-semibold" : ""}`}>{cell(r, c)}</td>)}
                <td className="py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setOpen(true); }} data-testid={`id-master-edit-${r.name}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <MasterDialog cfg={cfg} m={section} open={open} item={edit} templeId={templeId} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
